import type { QueryKey } from "@tanstack/react-query";
import type { BeneficiaryDetail } from "@/domain/beneficiary";
import type { InvestorWithInvestmentSummary } from "@/domain/investor";
import type { Tag, TagChanges, TagRef } from "@/domain/tag";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { arraySlot, isObject, type CacheEntry, type CacheSlot } from "../cache/cacheSlots";
import { defineEntityCache } from "../cache/entityCache";
import { beneficiaryListSlots } from "../beneficiaries/cache";
import { investorListSlots } from "../investors/cache";

function isTag(value: unknown): value is Tag {
  return isObject(value) && typeof value.uuid === "string" && isObject(value.usage);
}

export const tagCache = defineEntityCache<string, TagChanges>({
  entity: InvalidationEntity.TAG,
  slots: (orgId) => [
    arraySlot({
      prefix: queryKeys.tags.all(orgId),
      isRecord: isTag,
      idOf: (tag) => tag.uuid,
      merge: (tag, changes) => ({ ...tag, ...changes }),
    }),
  ],
  records: () => [],
});

/** Replaces the tags of the record wherever a cached response holds it, at any depth. */
export function withRecordTags(
  node: unknown,
  recordUuid: string,
  tags: readonly TagRef[],
): unknown {
  if (Array.isArray(node)) {
    let changed = false;
    const next = node.map((item) => {
      const updated = withRecordTags(item, recordUuid, tags);
      if (updated !== item) changed = true;
      return updated;
    });
    return changed ? next : node;
  }
  if (!isObject(node)) return node;
  if (node.uuid === recordUuid && Array.isArray(node.tags)) return { ...node, tags };
  let changed = false;
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    const updated = withRecordTags(value, recordUuid, tags);
    next[key] = updated;
    if (updated !== value) changed = true;
  }
  return changed ? next : node;
}

/** Every cached response under the prefix that holds the record, whatever its shape. */
function taggedSlot(prefix: QueryKey): CacheSlot<string, readonly TagRef[], never> {
  return {
    prefix,
    patchAll: (entries, id, tags) => {
      const changed: CacheEntry[] = [];
      for (const [key, data] of entries) {
        const next = withRecordTags(data, id, tags);
        if (next !== data) changed.push([key, next]);
      }
      return changed;
    },
    removeAll: () => [],
  };
}

const wearing = <R extends { tags: TagRef[] }>(row: R, tags: readonly TagRef[]): R => ({
  ...row,
  tags: [...tags],
});

/** The tags an investor wears: its list membership and footers, then every cached response. */
export const investorTagsCache = defineEntityCache<
  string,
  readonly TagRef[],
  InvestorWithInvestmentSummary
>({
  entity: InvalidationEntity.INVESTOR,
  row: wearing,
  slots: (orgId) => [
    ...investorListSlots(orgId, wearing<InvestorWithInvestmentSummary>),
    ...[queryKeys.investors.all(orgId), queryKeys.investorDropdown.all(orgId)].map(taggedSlot),
  ],
  records: (orgId, uuid) => [queryKeys.investors.record(orgId, uuid)],
});

/** The tags a beneficiary wears: its list membership and footers, then every cached response. */
export const beneficiaryTagsCache = defineEntityCache<string, readonly TagRef[], BeneficiaryDetail>(
  {
    entity: InvalidationEntity.BENEFICIARY,
    row: wearing,
    slots: (orgId) => [
      ...beneficiaryListSlots(orgId, wearing<BeneficiaryDetail>),
      ...[queryKeys.beneficiaries.all(orgId), queryKeys.beneficiaryDropdown.all(orgId)].map(
        taggedSlot,
      ),
    ],
    records: (orgId, uuid) => [queryKeys.beneficiaries.detail(orgId, uuid)],
  },
);
